import { useCallback, useEffect, useRef } from 'react'
import type { UseFormWatch, UseFormSetValue, UseFormRegister, FieldValues, Path } from 'react-hook-form'
import { fieldFormatters, type FieldFormatterType } from '@/shared/utils/formatters'

/**
 * Configuration for a single field to auto-format.
 */
interface FormatterFieldConfig<T extends FieldValues> {
  /** The form field name (must match a key in the form) */
  name: Path<T>
  /** The type of formatter to apply */
  formatter: FieldFormatterType
  /** When to apply the formatter. Default: 'change' (real-time) */
  trigger?: 'change' | 'blur'
}

interface UseFormFormatterReturn<T extends FieldValues> {
  /**
   * Wraps register() for blur-formatted fields, composing the formatter's onBlur
   * with react-hook-form's onBlur so both run.
   *
   * @example
   * ```tsx
   * <input {...registerWithFormatter('phone_number')} />
   * ```
   */
  registerWithFormatter: (name: Path<T>) => ReturnType<UseFormRegister<T>>
}

/**
 * Hook that watches form fields and applies formatters automatically.
 *
 * Fields with `trigger: 'change'` (default) format in real-time as the user types.
 * Fields with `trigger: 'blur'` format only when the input loses focus,
 * which is better for fields where real-time formatting interferes with editing
 * (e.g. phone numbers with auto-prepended prefixes).
 *
 * @example
 * ```tsx
 * const { register, watch, setValue, handleSubmit } = useForm<ClientFormData>({...})
 *
 * const { registerWithFormatter } = useFormFormatter(watch, setValue, register, [
 *   { name: 'rut', formatter: 'rut' },
 *   { name: 'name', formatter: 'name' },
 *   { name: 'email', formatter: 'email' },
 *   { name: 'phone_number', formatter: 'phone', trigger: 'blur' },
 * ])
 *
 * // For blur fields, use registerWithFormatter instead of register:
 * <input {...registerWithFormatter('phone_number')} />
 * ```
 */
export function useFormFormatter<T extends FieldValues>(
  watch: UseFormWatch<T>,
  setValue: UseFormSetValue<T>,
  register: UseFormRegister<T>,
  fields: FormatterFieldConfig<T>[],
): UseFormFormatterReturn<T> {
  // Track previous values to avoid infinite loops (for change-based fields)
  const prevValues = useRef<Record<string, string>>({})

  // Separate fields by trigger type
  const changeFields = fields.filter((f) => (f.trigger ?? 'change') === 'change')
  const blurFieldsMap = useRef(
    new Map(fields.filter((f) => f.trigger === 'blur').map((f) => [f.name as string, f.formatter])),
  )

  // Real-time formatting for change-triggered fields
  useEffect(() => {
    if (changeFields.length === 0) return

    const subscription = watch((formValues) => {
      for (const { name, formatter } of changeFields) {
        const raw = formValues[name as string] as string | undefined
        if (raw === undefined || raw === null) continue

        const rawStr = String(raw)
        const prev = prevValues.current[name as string]

        // Skip if value hasn't changed (avoids infinite setValue loops)
        if (rawStr === prev) continue

        const formatted = fieldFormatters[formatter](rawStr)

        // Only update if formatting actually changed the value
        if (formatted !== rawStr) {
          prevValues.current[name as string] = formatted
          setValue(name, formatted as T[Path<T>], { shouldValidate: false })
        } else {
          prevValues.current[name as string] = rawStr
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, setValue, changeFields])

  // Wraps register() for blur-formatted fields, composing both onBlur handlers
  const registerWithFormatter = useCallback(
    (name: Path<T>) => {
      const registered = register(name)
      const formatter = blurFieldsMap.current.get(name as string)

      if (!formatter) return registered

      const originalOnBlur = registered.onBlur

      return {
        ...registered,
        onBlur: async (event: { target: unknown; type?: string }) => {
          // Run formatter first
          const currentValue = watch(name)
          if (currentValue !== undefined && currentValue !== null) {
            const rawStr = String(currentValue)
            const formatted = fieldFormatters[formatter](rawStr)
            if (formatted !== rawStr) {
              setValue(name, formatted as T[Path<T>], { shouldValidate: true })
            }
          }

          // Then run react-hook-form's onBlur (validation, touched state, etc.)
          await originalOnBlur(event)
        },
      }
    },
    [register, watch, setValue],
  )

  return { registerWithFormatter }
}
